/**
 * CSAT — Zufriedenheitsbewertung, aktivierbar je Mandant (Spec 3.2 / 8.6).
 *
 * Der Ablauf in drei Zeilen: beim Schliessen eines Tickets (oder auf Klick)
 * entsteht eine `csat_responses`-Zeile **ohne** Bewertung, der Kunde bekommt
 * einen Einmal-Link, und erst seine Antwort fuellt `score`/`comment`.
 *
 * Der Link ist **signiert und einmalig**: er traegt einen zufaelligen Token
 * plus eine HMAC-Signatur darueber. Geprueft wird in dieser Reihenfolge —
 * Signatur (billig, ohne Datenbank), Ablaufdatum, „schon beantwortet". Genau
 * diese drei Faelle deckt der Testblock ab.
 *
 * Doku: docs/services/csat.md
 */
import { z } from 'zod';
import { uuidSchema } from './common.js';
import { ticketNumberSchema } from './ticket.js';
/** Ueber welchen Weg die Bitte um Bewertung ging (Spec 3.2: `channel`). */
export const CSAT_CHANNELS = ['email', 'app'];
export const csatChannelSchema = z.enum(CSAT_CHANNELS);
export const CSAT_MIN_SCORE = 1;
export const CSAT_MAX_SCORE = 5;
export const csatScoreSchema = z.number().int().min(CSAT_MIN_SCORE).max(CSAT_MAX_SCORE);
/** Wie lange ein Bewertungslink gilt. 30 Tage — danach ist die Frage veraltet. */
export const CSAT_LINK_TTL_DAYS = 30;
export const csatResponseSchema = z.object({
    id: uuidSchema,
    tenant_id: uuidSchema,
    ticket_id: uuidSchema,
    channel: csatChannelSchema,
    /** `null`, solange der Kunde nicht geantwortet hat. */
    score: csatScoreSchema.nullable(),
    comment: z.string().nullable(),
    requested_at: z.iso.datetime(),
    responded_at: z.iso.datetime().nullable(),
    expires_at: z.iso.datetime(),
    created_at: z.iso.datetime(),
    updated_at: z.iso.datetime(),
});
/**
 * Was die oeffentliche Mini-Seite sieht — **kein** Login, also so wenig wie
 * moeglich: Ticketnummer und Betreff, damit der Kunde weiss, worum es geht.
 * Kein Verlauf, keine Kontaktdaten, keine internen Felder.
 */
export const publicCsatSchema = z.object({
    ticket_number: ticketNumberSchema,
    subject: z.string(),
    tenant_name: z.string(),
    /** `true`, sobald eine Bewertung vorliegt — die Seite zeigt dann „Danke". */
    answered: z.boolean(),
    score: csatScoreSchema.nullable(),
});
export const submitCsatSchema = z.object({
    score: csatScoreSchema,
    comment: z.string().max(2000).nullable().default(null),
});
/** Auswertung je Queue (Spec 8.6: „Auswertung im Dashboard"). */
export const csatQueueSummarySchema = z.object({
    queue_id: uuidSchema,
    queue_name: z.string(),
    requested: z.number().int().min(0),
    answered: z.number().int().min(0),
    /** Durchschnitt der abgegebenen Bewertungen, auf eine Stelle gerundet. */
    average: z.number().nullable(),
});
export const csatCommentSchema = z.object({
    id: uuidSchema,
    ticket_id: uuidSchema,
    ticket_number: ticketNumberSchema,
    queue_id: uuidSchema,
    score: csatScoreSchema,
    comment: z.string().nullable(),
    responded_at: z.iso.datetime(),
});
export const csatSummarySchema = z.object({
    queues: z.array(csatQueueSummarySchema),
    /** Gesamtdurchschnitt ueber alle Queues — `null` ohne Bewertungen. */
    average: z.number().nullable(),
    answered: z.number().int().min(0),
    requested: z.number().int().min(0),
    /** Die juengsten Bewertungen mit Kommentar (Spec 8.6: „Verlauf, Kommentare"). */
    recent: z.array(csatCommentSchema),
});
//# sourceMappingURL=csat.js.map